import { useTranslations } from '@vocab/react';
import type { useToast } from 'braid-design-system';

import type { PageConfig } from 'src/libs/PageConfig';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import type { Bucket } from '../../stores/JobApplicationStore/types';

import translations from './.vocab';
import useHandleError from './useHandleError';
import { updateStatusOnServer } from './useHandleStatusChange';

interface Props {
  jobApplicationStore: IJobApplicationStore;
  destinationBucket: Bucket;
  onUpdateSuccess: () => Promise<void>;
  showToast: ReturnType<typeof useToast>;
  undoTrackUpdateStatus: () => void;
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

export interface UseHandleUndoStatusChangeResponse {
  handleUndoStatusChange: ({
    jobApplicationStore,
    destinationBucket,
    showToast,
    onUpdateSuccess,
  }: Props) => Promise<void>;
}

const useHandleUndoStatusChange = (
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
): UseHandleUndoStatusChangeResponse => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();
  const { handleError } = useHandleError(addCustomError);

  const handleUndoError = (showToast: ReturnType<typeof useToast>) => {
    showToast({
      tone: 'critical',
      message: t('Sorry, we couldn’t save your changes. Can we try again?'),
      key: 'STATUS_CHANGE_ERROR_TOAST_KEY',
      closeLabel: t('Close'),
    });
    addCustomError({
      error: 'UPDATE_STATUS',
      context: {
        description: 'undoState missing job application',
      },
    });
  };

  return {
    handleUndoStatusChange: async ({
      jobApplicationStore,
      destinationBucket,
      showToast,
      undoTrackUpdateStatus,
      onUpdateSuccess,
    }: Props) => {
      let jobApplication;
      try {
        jobApplication =
          jobApplicationStore.updateListStateAfterChangeStatusUndo();
      } catch {
        handleUndoError(showToast);
        return;
      }
      jobApplication.setIsChangingState(true);
      const success = await updateStatusOnServer(
        jobApplicationStore,
        jobApplication,
        destinationBucket,
        null,
        addCustomError,
      );
      jobApplication.setIsChangingState(false);

      if (!success) {
        handleError(
          showToast,
          jobApplication,
          'updateStatusForProspects returned success: false',
        );
        return;
      }

      jobApplication.setStatus(mapBucketTypeToBuckets(destinationBucket));

      undoTrackUpdateStatus();

      showToast({
        tone: 'positive',
        message: t('Undo Message', {
          firstName: jobApplication.firstName,
          newStatusLabel: getLabelFromBucket(
            mapBucketTypeToBuckets(destinationBucket),
          ),
        }),
        key: 'UNDO_STATUS_CHANGE_TOAST_KEY',
      });

      onUpdateSuccess();
    },
  };
};

export default useHandleUndoStatusChange;
